/**
 * SkillOverview - Sol kolon: About, What's New, Quick Start, Usage, Requirements
 */
import { Check, Copy, Package, Star, Terminal, Zap } from 'lucide-react'
import { useState } from 'react'
import type { Skill } from '../hooks/useSkills'

function CopyCode({ text }: { text: string }) {
  const [ok, setOk] = useState(false)
  return (
    <button onClick={() => { void navigator.clipboard.writeText(text); setOk(true); setTimeout(()=>setOk(false),2000) }}
      style={{ display:'flex', alignItems:'center', gap:'5px', padding:'5px 10px', borderRadius:'6px', fontSize:'11px', fontWeight:600, background: ok?'rgba(34,197,94,.15)':'rgba(255,255,255,.08)', color: ok?'#22c55e':'var(--text-secondary)', border:`1px solid ${ok?'rgba(34,197,94,.3)':'rgba(255,255,255,.12)'}`, cursor:'pointer' }}>
      {ok?<Check size={11}/>:<Copy size={11}/>} {ok?'Copied!':'Copy'}
    </button>
  )
}

function Card({ children }: { children: React.ReactNode }) {
  return <div style={{ padding:'20px', borderRadius:'12px', background:'var(--card-bg)', border:'1px solid var(--border-color)' }}>{children}</div>
}

function CardTitle({ icon, label, color }: { icon: React.ReactNode; label: string; color?: string }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'12px', fontSize:'13px', fontWeight:700, color: color ?? 'var(--text-primary)' }}>
      {icon} {label}
    </div>
  )
}

interface Props {
  skill: Skill
  currentChangelog: string
  onInstall: () => void
}

export default function SkillOverview({ skill, currentChangelog, onInstall }: Props) {
  const accent = skill.color ?? '#3b82f6'
  const cli = `aicloud skill install ${skill.name.toLowerCase().replace(/\s+/g,'_')}`
  const cliRun = `aicloud skill run ${skill.name.toLowerCase().replace(/\s+/g,'_')} '{"query":"example"}'`

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>

      {/* About */}
      <Card>
        <CardTitle icon={<Zap size={13} color={accent}/>} label="About"/>
        <p style={{ fontSize:'13px', lineHeight:'1.7', color:'var(--text-secondary)', margin:0 }}>
          {skill.documentation ?? skill.description}
        </p>
      </Card>

      {/* What's New */}
      {currentChangelog && (
        <Card>
          <CardTitle icon={<Star size={13} color="#f59e0b"/>} label={`What's New in v${skill.version}`}/>
          <p style={{ fontSize:'13px', lineHeight:'1.7', color:'var(--text-secondary)', margin:0 }}>{currentChangelog}</p>
        </Card>
      )}

      {/* Quick Start */}
      <Card>
        <CardTitle icon={<Terminal size={13} color={accent}/>} label="Quick Start"/>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
          {/* Web */}
          <div style={{ padding:'14px', borderRadius:'10px', background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)' }}>
            <div style={{ fontSize:'10px', fontWeight:700, color:'var(--text-secondary)', marginBottom:'8px', textTransform:'uppercase', letterSpacing:'.5px' }}>Web</div>
            <button onClick={onInstall} style={{ width:'100%', padding:'8px', borderRadius:'8px', fontSize:'12px', fontWeight:600, background: skill.is_installed?`${accent}20`:accent, color: skill.is_installed?accent:'#fff', border: skill.is_installed?`1px solid ${accent}40`:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'5px' }}>
              {skill.is_installed ? <><Check size={12}/> Installed</> : <><Package size={12}/> Install</>}
            </button>
          </div>
          {/* CLI */}
          <div style={{ padding:'14px', borderRadius:'10px', background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)' }}>
            <div style={{ fontSize:'10px', fontWeight:700, color:'var(--text-secondary)', marginBottom:'8px', textTransform:'uppercase', letterSpacing:'.5px' }}>CLI</div>
            <code style={{ display:'block', fontSize:'10px', padding:'7px', borderRadius:'6px', background:'rgba(0,0,0,.35)', color:'#a3e635', fontFamily:'monospace', wordBreak:'break-all', lineHeight:'1.5' }}>{cli}</code>
          </div>
        </div>
      </Card>

      {/* Usage Example */}
      {skill.usage_example && (
        <Card>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px' }}>
            <CardTitle icon={<Terminal size={13} color={accent}/>} label="Usage Example"/>
            <CopyCode text={skill.usage_example}/>
          </div>
          <pre style={{ margin:0, padding:'14px', borderRadius:'8px', background:'rgba(0,0,0,.4)', color:'#a3e635', fontSize:'12px', fontFamily:'monospace', overflowX:'auto', lineHeight:'1.6', whiteSpace:'pre-wrap', wordBreak:'break-word' }}>
            {skill.usage_example}
          </pre>
          <div style={{ marginTop:'10px' }}>
            <div style={{ fontSize:'11px', color:'var(--text-secondary)', marginBottom:'5px' }}>Run via CLI:</div>
            <code style={{ display:'block', fontSize:'11px', padding:'8px', borderRadius:'6px', background:'rgba(0,0,0,.3)', color:'#60a5fa', fontFamily:'monospace' }}>{cliRun}</code>
          </div>
        </Card>
      )}

      {/* Requirements */}
      {skill.requirements && skill.requirements.length > 0 && (
        <Card>
          <CardTitle icon={<Package size={13} color={accent}/>} label="Requirements"/>
          <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
            {skill.requirements.map((r,i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'7px 12px', borderRadius:'8px', background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.07)' }}>
                <Check size={11} color="#22c55e"/>
                <code style={{ fontSize:'12px', fontFamily:'monospace', color:'var(--text-primary)' }}>{r}</code>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}
